import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarCheck,
  faCreditCard,
  faFileInvoice,
  faHeadset,
  faPaperPlane,
  faRotateRight,
  faTimes,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import supportService, {
  CustomerSupportRecord,
  CustomerSupportReply,
} from '../../services/supportService';
import paymentService from '../../services/paymentService';
import './CustomerSupportChat.css';

type Destination = NonNullable<CustomerSupportReply['destination']>;

const SUPPORTED_BOOKING_ACTIONS = new Set(['join', 'reschedule', 'cancel', 'pay']);

interface ChatMessage {
  id: number;
  role: 'assistant' | 'user';
  answer: string;
  destination?: CustomerSupportReply['destination'];
  intent?: string;
  records?: CustomerSupportRecord[];
  retryText?: string;
  ticketCategory?: string;
  ticketSubject?: string;
}

const DESTINATIONS: Record<Destination, { path: string; labelKey: string }> = {
  payments: { path: '/my-transactions', labelKey: 'assistant.openPayments' },
  bookings: { path: '/my-bookings', labelKey: 'assistant.openBookings' },
  services: { path: '/', labelKey: 'assistant.browseServices' },
  teleconsultation: { path: '/teleconsultation', labelKey: 'assistant.openTeleconsultation' },
  profile: { path: '/profile', labelKey: 'assistant.openProfile' },
  ai_family_doctor: { path: '/ai-family-doctor', labelKey: 'assistant.openFamilyDoctor' },
  support: { path: '/support?create=1', labelKey: 'assistant.raiseTicket' },
};

const isInternalPath = (path: string): boolean => path.startsWith('/') && !path.startsWith('//');

const FOLLOW_UP_PROMPTS: Record<string, string[]> = {
  payment_status: ['invoicePrompt', 'refundPrompt'],
  invoice_status: ['paymentPrompt', 'refundPrompt'],
  invoice_help: ['paymentPrompt', 'refundPrompt'],
  payment_help: ['paymentPrompt', 'invoicePrompt'],
  refund_help: ['paymentPrompt', 'contactSupportPrompt'],
  booking_status: ['bookingHelpPrompt', 'contactSupportPrompt'],
  booking_help: ['bookingPrompt', 'contactSupportPrompt'],
  booking_status_help: ['bookingPrompt', 'contactSupportPrompt'],
  technical_help: ['contactSupportPrompt', 'accountPrompt'],
  account_help: ['contactSupportPrompt', 'accountPrompt'],
};

const formatDate = (value: string | null, locale: string): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
};

const statusTone = (status: string): 'success' | 'pending' | 'danger' | 'neutral' => {
  const normalized = status.toLowerCase();
  if (['success', 'successful', 'confirmed', 'completed', 'paid', 'attended', 'registered'].includes(normalized)) {
    return 'success';
  }
  if (['initiated', 'pending', 'pending_payment', 'in_progress', 'unpaid'].includes(normalized)) {
    return 'pending';
  }
  if (['failed', 'refunded', 'cancelled', 'no_show'].includes(normalized)) {
    return 'danger';
  }
  return 'neutral';
};

const CustomerSupportChat: React.FC = () => {
  const { t, i18n } = useTranslation(['support', 'common']);
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [invoiceBusy, setInvoiceBusy] = useState<string | null>(null);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const panelRef = useRef<HTMLElement>(null);
  const messageListRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const nextId = useRef(0);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{ id: nextId.current++, role: 'assistant', answer: t('support:assistant.welcome') }]);
    }
  }, [messages.length, t]);

  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    } else if (wasOpen.current) {
      launcherRef.current?.focus();
    }
    wasOpen.current = open;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current?.contains(document.activeElement)) return;
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), [href], select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const closeChat = () => {
    setOpen(false);
  };

  const sendMessage = async (text: string, retryId?: number) => {
    const message = text.trim();
    if (!message || sending) return;

    setMessages((current) => {
      const withoutFailedReply = retryId === undefined
        ? current
        : current.filter((entry) => entry.id !== retryId);
      return retryId === undefined
        ? [...withoutFailedReply, { id: nextId.current++, role: 'user', answer: message }]
        : withoutFailedReply;
    });
    setInput('');
    setSending(true);

    try {
      const language = i18n.language.replace('_', '-').split('-')[0];
      const reply = await supportService.chat(message, language);
      setMessages((current) => [
        ...current,
        {
          id: nextId.current++,
          role: 'assistant',
          answer: reply.answer,
          destination: reply.destination,
          intent: reply.intent,
          records: reply.records,
          ticketCategory: reply.ticket_category,
          ticketSubject: reply.ticket_subject,
        },
      ]);
    } catch {
      setMessages((current) => [
        ...current,
        {
          id: nextId.current++,
          role: 'assistant',
          answer: t('support:assistant.requestFailed'),
          destination: 'support',
          retryText: message,
          ticketCategory: 'general_inquiry',
          ticketSubject: t('support:assistant.genericTicketSubject'),
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void sendMessage(input);
  };

  const openDestination = (message: ChatMessage) => {
    const destination = message.destination;
    if (!destination) return;
    let path = DESTINATIONS[destination].path;
    if (destination === 'support') {
      const query = new URLSearchParams({ create: '1' });
      if (message.ticketCategory) query.set('category', message.ticketCategory);
      if (message.ticketSubject) query.set('subject', message.ticketSubject.slice(0, 120));
      path = `/support?${query.toString()}`;
    }
    navigate(path);
    closeChat();
  };

  const downloadInvoice = async (reference: string) => {
    setInvoiceBusy(reference);
    setInvoiceError(null);
    try {
      await paymentService.downloadInvoice(reference);
    } catch {
      setInvoiceError(reference);
    } finally {
      setInvoiceBusy(null);
    }
  };

  if (location.pathname.startsWith('/ai-family-doctor') || location.pathname.startsWith('/admin')) {
    return null;
  }

  return (
    <div className="customer-support-chat">
      {open && (
        <section ref={panelRef} className="csc-panel" role="dialog" aria-modal="true" aria-label={t('support:assistant.title')}>
          <header className="csc-header">
            <div className="csc-header-brand">
              <div className="csc-header-icon">
                <FontAwesomeIcon icon={faHeadset} />
                <span className="csc-online-indicator" />
              </div>
              <div className="csc-header-copy">
                <strong>{t('support:assistant.title')}</strong>
                <span>{t('support:assistant.subtitle')}</span>
              </div>
            </div>
            <span className="csc-online-badge">
              <span />
              {t('support:assistant.available')}
            </span>
            <button type="button" className="csc-close" onClick={closeChat} aria-label={t('common:buttons.close')}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </header>

          <div className="csc-messages" ref={messageListRef} aria-live="polite" aria-relevant="additions">
            {messages.map((message) => (
              <div key={message.id} className={`csc-message-row csc-message-row-${message.role}`}>
                <span className="csc-message-avatar" aria-hidden="true">
                  <FontAwesomeIcon icon={message.role === 'assistant' ? faHeadset : faUser} />
                </span>
                <article className={`csc-message csc-message-${message.role}`}>
                  <p>{message.answer}</p>
                  {message.records?.map((record) => (
                    <div className="csc-record" key={`${record.kind}-${record.reference}`}>
                      <strong>{record.title}</strong>
                      <span>{record.service ? `${record.service} · ` : ''}{record.reference}</span>
                      <span>{t('support:assistant.date')}: {formatDate(record.date, i18n.language)}</span>
                      <span className="csc-record-status-line">
                        {t('support:assistant.status')}:
                        <strong className={`csc-status-badge csc-status-${statusTone(record.status)}`}>
                          {record.status_label}
                        </strong>
                      </span>
                      {record.payment_status_label && (
                        <span className="csc-record-status-line">
                          {t('support:assistant.paymentStatus')}:
                          <strong className={`csc-status-badge csc-status-${statusTone(record.payment_status || '')}`}>
                            {record.payment_status_label}
                          </strong>
                        </span>
                      )}
                      {record.amount && (
                        <span>{t('support:assistant.amount')}: {record.currency} {record.amount}</span>
                      )}
                      {record.kind === 'booking'
                        && isInternalPath(record.detail_url)
                        && !!record.actions?.some((action) => SUPPORTED_BOOKING_ACTIONS.has(action)) && (
                          <div className="csc-record-action-group">
                            <span className="csc-record-actions-label">
                              {t('support:assistant.availableActions')}
                            </span>
                            <div className="csc-record-action-list">
                              {record.actions
                                .filter((action) => SUPPORTED_BOOKING_ACTIONS.has(action))
                                .map((action) => {
                                  const label = t(`support:assistant.actionLabels.${action}`, action);
                                  return (
                                    <button
                                      key={action}
                                      type="button"
                                      className={`csc-booking-action csc-booking-action-${action}`}
                                      aria-label={`${label}: ${record.title}`}
                                      onClick={() => {
                                        navigate(record.detail_url);
                                        closeChat();
                                      }}
                                    >
                                      {label}
                                    </button>
                                  );
                                })}
                            </div>
                          </div>
                        )}
                      {isInternalPath(record.detail_url) && (
                        <button
                          type="button"
                          className="csc-link-button"
                          onClick={() => {
                            navigate(record.detail_url);
                            closeChat();
                          }}
                        >
                          {t('support:assistant.viewDetails')}
                        </button>
                      )}
                      {record.kind === 'payment' && record.invoice_available && (
                        <button
                          type="button"
                          className="csc-link-button csc-invoice-button"
                          disabled={invoiceBusy === record.reference}
                          onClick={() => void downloadInvoice(record.reference)}
                        >
                          <FontAwesomeIcon icon={faFileInvoice} />
                          {invoiceBusy === record.reference
                            ? t('support:assistant.invoiceDownloading')
                            : t('support:assistant.downloadInvoice')}
                        </button>
                      )}
                      {record.kind === 'payment' && invoiceError === record.reference && record.invoice_available && (
                        <span className="csc-inline-error" role="alert">
                          {t('support:assistant.invoiceFailed')}
                        </span>
                      )}
                    </div>
                  ))}
                  {message.destination && (
                    <button
                      type="button"
                      className="csc-link-button"
                      onClick={() => {
                        openDestination(message);
                      }}
                    >
                      {t(`support:${DESTINATIONS[message.destination].labelKey}`)}
                    </button>
                  )}
                  {message.retryText && (
                    <button
                      type="button"
                      className="csc-link-button csc-retry-button"
                      onClick={() => void sendMessage(message.retryText || '', message.id)}
                      disabled={sending}
                    >
                      <FontAwesomeIcon icon={faRotateRight} />
                      {t('support:assistant.retry')}
                    </button>
                  )}
                  {message.role === 'assistant' && message.intent && FOLLOW_UP_PROMPTS[message.intent] && (
                    <div className="csc-follow-ups">
                      <span>{t('support:assistant.followUp')}</span>
                      {FOLLOW_UP_PROMPTS[message.intent].map((promptKey) => (
                        <button
                          key={promptKey}
                          type="button"
                          onClick={() => void sendMessage(t(`support:assistant.${promptKey}`))}
                          disabled={sending}
                        >
                          {t(`support:assistant.${promptKey}`)}
                        </button>
                      ))}
                    </div>
                  )}
                  {messages.length === 1 && message.role === 'assistant' && (
                    <div className="csc-suggestions" aria-label={t('support:assistant.quickQuestions')}>
                      <span>{t('support:assistant.quickQuestions')}</span>
                      <button type="button" onClick={() => void sendMessage(t('support:assistant.paymentPrompt'))}>
                        <FontAwesomeIcon icon={faCreditCard} />
                        {t('support:assistant.paymentSuggestion')}
                      </button>
                      <button type="button" onClick={() => void sendMessage(t('support:assistant.bookingPrompt'))}>
                        <FontAwesomeIcon icon={faCalendarCheck} />
                        {t('support:assistant.bookingSuggestion')}
                      </button>
                      <button type="button" onClick={() => void sendMessage(t('support:assistant.invoicePrompt'))}>
                        <FontAwesomeIcon icon={faFileInvoice} />
                        {t('support:assistant.invoiceSuggestion')}
                      </button>
                      <button type="button" onClick={() => void sendMessage(t('support:assistant.refundPrompt'))}>
                        <FontAwesomeIcon icon={faCreditCard} />
                        {t('support:assistant.refundSuggestion')}
                      </button>
                    </div>
                  )}
                </article>
              </div>
            ))}
            {sending && (
              <div className="csc-typing" role="status">
                <span className="csc-typing-dots" aria-hidden="true"><i /><i /><i /></span>
                {t('support:assistant.thinking')}
              </div>
            )}
          </div>

          <form className="csc-input-row" onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              maxLength={1000}
              placeholder={t('support:assistant.placeholder')}
              aria-label={t('support:assistant.inputLabel')}
              disabled={sending}
            />
            <button type="submit" disabled={!input.trim() || sending} aria-label={t('support:assistant.send')}>
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </form>
        </section>
      )}
      {!open && (
        <button
          type="button"
          className="csc-launcher"
          ref={launcherRef}
          onClick={() => setOpen(true)}
          aria-label={t('support:assistant.launch')}
          title={t('support:assistant.launch')}
        >
          <FontAwesomeIcon icon={faHeadset} />
        </button>
      )}
    </div>
  );
};

export default CustomerSupportChat;
